import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { MessageCircle, X, Phone, Mail, ArrowRight, MessagesSquare, Send } from 'lucide-react';
import { getFloatingItems, resolveLinkHref } from '../utils/floatingContactItems';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_ICON = { phone: Phone, email: Mail, chat: MessagesSquare, link: ArrowRight };

/**
 * Plovoucí kontaktní tlačítko zobrazené v pravém dolním rohu stránky.
 * Konfigurace se předává z CMS přes prop `config` (cmsConfig.settings.floatingContact).
 */
const FloatingContactButton = ({ config = {} }) => {
  const [open, setOpen] = useState(false);
  const [show, setShow] = useState(false);
  const [cookieBarVisible, setCookieBarVisible] = useState(() =>
    typeof document !== 'undefined' && document.body?.getAttribute('data-cookie-bar') === 'visible'
  );
  const [view, setView] = useState('menu'); // 'menu' | 'chat-name' | 'chat'
  const [chatEnabled, setChatEnabled] = useState(false);
  const [visitorName, setVisitorName] = useState(() => localStorage.getItem('tg_visitor_name') || '');
  const [visitorEmail, setVisitorEmail] = useState(() => localStorage.getItem('tg_visitor_email') || '');
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('tg_session_id') || '');
  const [visitorCode, setVisitorCode] = useState(() => localStorage.getItem('tg_visitor_code') || '');
  const [messages, setMessages] = useState(() => {
    try { return JSON.parse(localStorage.getItem('tg_messages') || '[]'); } catch { return []; }
  });
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(false);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const lastTsRef = useRef('');

  // Defaults — used when CMS config is missing or partial
  const enabled = config.enabled !== false; // default ON
  const showAfterScroll = config.showAfterScroll ?? 400;
  const headerTitle = config.headerTitle || 'Kontaktujte nás';
  const headerSubtitle = config.headerSubtitle || 'Jsme tu pro vás';
  const headerBgFrom = config.headerBgFrom || '#2563eb';
  const headerBgTo = config.headerBgTo || '#4f46e5';
  const headerTextColor = config.headerTextColor || '#ffffff';
  const buttonBg = config.buttonBg || '#2563eb';
  const buttonHoverBg = config.buttonHoverBg || '#3b82f6';

  // Items rendered in the menu — supports both the new `items` array and the
  // legacy individual fields (auto-migrated by getFloatingItems).
  const items = getFloatingItems(config).filter((it) => it.enabled !== false && (it.type !== 'chat' || chatEnabled));

  useEffect(() => {
    const onScroll = () => {
      setShow(window.scrollY > showAfterScroll);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [showAfterScroll]);

  // Listen for cookie-bar visibility so we can lift this button above it
  useEffect(() => {
    const onChange = (e) => setCookieBarVisible(!!e.detail?.visible);
    window.addEventListener('cookie-bar-visibility', onChange);
    return () => window.removeEventListener('cookie-bar-visibility', onChange);
  }, []);

  // Check if Telegram chat is enabled (public status)
  useEffect(() => {
    let cancelled = false;
    const check = () => axios.get(`${API}/telegram/status`)
      .then(({ data }) => { if (!cancelled) setChatEnabled(!!data?.enabled); })
      .catch(() => { if (!cancelled) setChatEnabled(false); });
    check();
    window.addEventListener('focus', check);
    const timer = setInterval(check, 30000);
    return () => { cancelled = true; clearInterval(timer); window.removeEventListener('focus', check); };
  }, []);

  // Persist messages
  useEffect(() => {
    if (messages.length) {
      localStorage.setItem('tg_messages', JSON.stringify(messages.slice(-100)));
    }
  }, [messages]);

  // Autoscroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, view]);

  const pollMessages = useCallback(async () => {
    if (!sessionId) return;
    try {
      const params = { session: sessionId };
      if (lastTsRef.current) params.since = lastTsRef.current;
      const { data } = await axios.get(`${API}/telegram/chat/poll`, { params });
      const newMsgs = data?.messages || [];
      if (newMsgs.length) {
        setMessages((prev) => {
          const ids = new Set(prev.map((m) => m.id));
          const add = newMsgs.filter((m) => !ids.has(m.id));
          if (!add.length) return prev;
          const merged = [...prev, ...add];
          lastTsRef.current = merged.reduce((acc, m) => (m.ts > acc ? m.ts : acc), lastTsRef.current);
          return merged;
        });
      }
      if (data?.status === 'closed') {
        // Reset session
        setSessionId('');
        setVisitorCode('');
        localStorage.removeItem('tg_session_id');
        localStorage.removeItem('tg_visitor_code');
      }
    } catch (e) {
      // ignore
    }
  }, [sessionId]);

  // Polling lifecycle
  useEffect(() => {
    if (!chatEnabled || !sessionId || !open || view !== 'chat') {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    pollMessages();
    pollRef.current = setInterval(pollMessages, 4000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [chatEnabled, sessionId, open, view, pollMessages]);

  // When opening, if we already have a session, jump straight to chat
  useEffect(() => {
    if (!open) return;
    if (chatEnabled && sessionId && visitorName) {
      setView('chat');
    } else {
      setView('menu');
    }
  }, [open, chatEnabled]);

  useEffect(() => {
    if (!chatEnabled && view !== 'menu') { setView('menu'); setOpen(false); }
  }, [chatEnabled, view]);

  const startChat = async () => {
    if (!chatEnabled) return;
    const name = visitorName.trim();
    const email = visitorEmail.trim();
    if (!name) return;
    setStarting(true);
    try {
      const { data } = await axios.post(`${API}/telegram/chat/start`, {
        visitor_name: name,
        visitor_email: email,
      });
      setSessionId(data.session_id);
      setVisitorCode(data.visitor_code);
      localStorage.setItem('tg_session_id', data.session_id);
      localStorage.setItem('tg_visitor_code', data.visitor_code);
      localStorage.setItem('tg_visitor_name', name);
      if (email) localStorage.setItem('tg_visitor_email', email);
      setMessages([{
        id: `sys-${Date.now()}`,
        from: 'system',
        text: `Vítejte, ${name}! Brzy se vám ozveme.`,
        ts: new Date().toISOString(),
      }]);
      setView('chat');
    } catch (e) {
      const detail = e.response?.data?.detail || 'Chat se nepodařilo zahájit.';
      setMessages([{ id: `err-${Date.now()}`, from: 'system', text: detail, ts: new Date().toISOString() }]);
      setView('chat');
    } finally {
      setStarting(false);
    }
  };

  const sendMessage = async () => {
    if (!chatEnabled) return;
    const text = draft.trim();
    if (!text || !sessionId || sending) return;
    setSending(true);
    const tempId = `local-${Date.now()}`;
    const local = { id: tempId, from: 'visitor', text, ts: new Date().toISOString() };
    setMessages((p) => [...p, local]);
    setDraft('');
    try {
      await axios.post(`${API}/telegram/chat/message`, { session_id: sessionId, text });
    } catch (e) {
      setMessages((p) => p.map((m) => m.id === tempId ? { ...m, error: true } : m));
    } finally {
      setSending(false);
    }
  };

  const closeChat = async () => {
    if (sessionId) {
      try { await axios.post(`${API}/telegram/chat/close`, { session_id: sessionId }); } catch {}
    }
    localStorage.removeItem('tg_session_id');
    localStorage.removeItem('tg_visitor_code');
    localStorage.removeItem('tg_messages');
    setSessionId('');
    setVisitorCode('');
    setMessages([]);
    setView('menu');
  };

  if (!enabled || !show || items.length === 0) return null;

  const renderMenu = () => (
    <div className="p-3 space-y-1" data-testid="floating-contact-menu-list">
      {items.map((it) => {
        const Icon = TYPE_ICON[it.type] || ArrowRight;
        const iconNode = (
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: it.iconBg }}>
            <Icon className="h-4 w-4" style={{ color: it.iconColor }} />
          </div>
        );
        const bodyNode = (
          <div>
            <p className="text-sm font-medium text-slate-900">{it.label}</p>
            {it.subLabel && <p className="text-xs text-slate-500">{it.subLabel}</p>}
          </div>
        );

        if (it.type === 'phone') {
          const num = (it.subLabel || '').trim();
          if (!num) return null;
          return (
            <a
              key={it.id}
              href={`tel:${num.replace(/\s+/g, '')}`}
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors"
              data-testid="floating-contact-phone"
            >
              {iconNode}{bodyNode}
            </a>
          );
        }

        if (it.type === 'email') {
          const addr = (it.subLabel || '').trim();
          if (!addr) return null;
          return (
            <a
              key={it.id}
              href={`mailto:${addr}`}
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors"
              data-testid="floating-contact-email"
            >
              {iconNode}{bodyNode}
            </a>
          );
        }

        if (it.type === 'chat') {
          if (!chatEnabled) return null;
          return (
            <button
              key={it.id}
              type="button"
              onClick={() => setView(sessionId && visitorName ? 'chat' : 'chat-name')}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-amber-50 transition-colors text-left"
              data-testid="floating-contact-chat"
            >
              {iconNode}{bodyNode}
            </button>
          );
        }

        if (it.type === 'link') {
          const href = resolveLinkHref(it);
          if (!href) return null;
          const external = /^https?:\/\//i.test(href);
          return (
            <a
              key={it.id}
              href={href}
              target={external ? '_blank' : undefined}
              rel={external ? 'noopener noreferrer' : undefined}
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-blue-50 transition-colors"
              data-testid="floating-contact-form"
            >
              {iconNode}{bodyNode}
            </a>
          );
        }

        return null;
      })}
    </div>
  );

  const renderNameForm = () => (
    <form
      className="p-4 space-y-3"
      onSubmit={(e) => { e.preventDefault(); startChat(); }}
      data-testid="floating-chat-name-form"
    >
      <div>
        <label className="block text-sm font-medium text-slate-800 mb-1">Jak Vás můžeme oslovovat?</label>
        <input
          type="text"
          value={visitorName}
          onChange={(e) => setVisitorName(e.target.value)}
          placeholder="Vaše jméno"
          maxLength={120}
          required
          autoFocus
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
          data-testid="floating-chat-name-input"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-800 mb-1">
          E-mail <span className="text-xs font-normal text-slate-500">(volitelně)</span>
        </label>
        <input
          type="email"
          value={visitorEmail}
          onChange={(e) => setVisitorEmail(e.target.value)}
          placeholder="vas@email.cz"
          maxLength={200}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
          data-testid="floating-chat-email-input"
        />
        <p className="text-[11px] text-slate-500 mt-1">Pokud opustíte stránku, ozveme se Vám e-mailem.</p>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setView('menu')}
          className="flex-1 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
        >
          Zpět
        </button>
        <button
          type="submit"
          disabled={!visitorName.trim() || starting}
          className="flex-1 py-2 text-sm text-white rounded-lg disabled:opacity-50"
          style={{ backgroundColor: buttonBg }}
          data-testid="floating-chat-start-btn"
        >
          {starting ? 'Spouštím…' : 'Spustit chat'}
        </button>
      </div>
    </form>
  );

  const renderChat = () => (
    <div className="flex flex-col" style={{ height: 400 }} data-testid="floating-chat-window">
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50"
      >
        {messages.map((m) => {
          if (m.from === 'system') {
            return (
              <div key={m.id} className="text-xs text-center text-slate-500 italic py-1">{m.text}</div>
            );
          }
          const mine = m.from === 'visitor';
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`px-3 py-2 rounded-2xl text-sm max-w-[80%] whitespace-pre-wrap break-words ${mine ? 'rounded-br-sm text-white' : 'rounded-bl-sm bg-white text-slate-800 border border-slate-200'}`}
                style={mine ? { backgroundColor: buttonBg, opacity: m.error ? 0.6 : 1 } : {}}
              >
                {m.text}
                {m.error && <div className="text-xs mt-1 opacity-80">⚠ Neodesláno</div>}
              </div>
            </div>
          );
        })}
        {messages.length === 0 && (
          <div className="text-xs text-center text-slate-400 py-6">Začněte konverzaci…</div>
        )}
      </div>
      <form
        onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
        className="border-t border-slate-200 p-2 flex gap-2 bg-white"
        data-testid="floating-chat-input-form"
      >
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Napište zprávu…"
          className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
          maxLength={4000}
          data-testid="floating-chat-input"
        />
        <button
          type="submit"
          disabled={!draft.trim() || sending}
          className="w-10 h-10 flex items-center justify-center rounded-lg text-white disabled:opacity-40"
          style={{ backgroundColor: buttonBg }}
          data-testid="floating-chat-send"
        >
          <Send className="h-4 w-4" />
        </button>
      </form>
      <div className="px-3 py-1.5 text-[10px] text-slate-400 flex items-center justify-between bg-slate-50 border-t border-slate-200">
        <span>{visitorCode ? `Kód: ${visitorCode}` : ''}</span>
        <button
          type="button"
          onClick={closeChat}
          className="hover:text-slate-700 underline"
          data-testid="floating-chat-close-btn"
        >
          Ukončit chat
        </button>
      </div>
    </div>
  );

  const headerLabel = view === 'menu' ? headerTitle : view === 'chat-name' ? 'Živý chat' : `Chat — ${visitorName}`;
  const headerSubLabel = view === 'menu' ? headerSubtitle : view === 'chat-name' ? 'Zadejte své jméno' : 'Odpovíme co nejdříve';

  return (
    <div
      className="fixed right-6 z-[110] transition-all duration-300"
      style={{ bottom: cookieBarVisible ? '8.5rem' : '1.5rem' }}
      data-testid="floating-contact"
    >
      {open && (
        <div
          data-testid="floating-contact-menu"
          className="absolute bottom-16 right-0 w-80 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden mb-2"
          style={{ animation: 'fadeInUp 0.2s ease-out' }}
        >
          <div
            className="p-4 flex items-center justify-between"
            style={{
              backgroundImage: `linear-gradient(to right, ${headerBgFrom}, ${headerBgTo})`,
              color: headerTextColor,
            }}
          >
            <div>
              <h4 className="font-semibold text-sm">{headerLabel}</h4>
              <p className="text-xs opacity-80 mt-1">{headerSubLabel}</p>
            </div>
            {view !== 'menu' && (
              <button
                type="button"
                onClick={() => setView('menu')}
                className="text-xs px-2 py-1 rounded bg-white/20 hover:bg-white/30"
                style={{ color: headerTextColor }}
                data-testid="floating-chat-back-btn"
              >
                ← Menu
              </button>
            )}
          </div>
          {view === 'menu' && renderMenu()}
          {view === 'chat-name' && renderNameForm()}
          {view === 'chat' && renderChat()}
        </div>
      )}

      <button
        data-testid="floating-contact-btn"
        onClick={() => setOpen(!open)}
        className={`relative w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 ${
          open ? 'rotate-0' : 'floating-btn-pulse'
        }`}
        style={{
          backgroundColor: open ? '#475569' : buttonBg,
        }}
        onMouseEnter={(e) => { if (!open) e.currentTarget.style.backgroundColor = buttonHoverBg; }}
        onMouseLeave={(e) => { if (!open) e.currentTarget.style.backgroundColor = buttonBg; }}
      >
        {open ? (
          <X className="h-5 w-5 text-white" />
        ) : (
          <MessageCircle className="h-5 w-5 text-white" />
        )}
      </button>
    </div>
  );
};

export default FloatingContactButton;
